import {
  HttpClient,
  HttpEventType,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { computed, inject, Injectable, resource } from '@angular/core';
import { lastValueFrom, map, Observable } from 'rxjs';
import { LogsService } from '../logs/logs.service';
import { RegionService } from '../region/region.service';

@Injectable({
  providedIn: 'root',
})
export class BuyerManagementService {
  http = inject(HttpClient);
  logs = inject(LogsService);
  regions = inject(RegionService);

  buyerImportHistory = resource({
    params: () => this.regions.selectedRegionId(),
    loader: ({ params }) =>
      lastValueFrom(this.logs.getLog$('BuyerImport', params)),
  });

  noBuyerImportHistory = computed(
    () => this.buyerImportHistory.status() === 'error',
  );

  buyerAddressImportHistory = resource({
    params: () => this.regions.selectedRegionId(),
    loader: ({ params }) =>
      lastValueFrom(this.logs.getLog$('BuyerAddressImport', params)),
  });

  noBuyerAddressImportHistory = computed(
    () => this.buyerAddressImportHistory.status() === 'error',
  );

  buyerLoginImportHistory = resource({
    params: () => this.regions.selectedRegionId(),
    loader: ({ params }) =>
      lastValueFrom(
        this.logs.getLogByMappedName$('Generic File Import-BuyerLogin', params),
      ),
  });

  noBuyerLoginImportHistory = computed(
    () => this.buyerLoginImportHistory.status() === 'error',
  );

  downloadBuyersBySelectedRegion(): Observable<HttpResponse<Blob>> {
    return this.http.get(
      `Buyer/download?regionId=${this.regions.selectedRegionId()}`,
      {
        responseType: 'blob',
        observe: 'response',
      },
    );
  }

  uploadBuyers = (file: File, businessEntity: string) => {
    const formData = new FormData();
    formData.append('file', file, file.name);
    const url = `genericdataimport/uploadfile?dataType=Buyer&entityCode=${businessEntity}&regionId=${this.regions.selectedRegionId()}`;
    const queryParams = new HttpParams();
    queryParams.set('dataType', 'Buyer');
    queryParams.set('entityCode', businessEntity);

    return this.http
      .post(url, formData, {
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        map((event) => {
          if (event.type === HttpEventType.UploadProgress) {
            return Math.round((100 * event.loaded) / (event.total || 1));
          } else {
            return 0;
          }
        }),
      );
  };

  uploadAddressBuyers = (file: File, businessEntity: string) => {
    const formData = new FormData();
    formData.append('file', file, file.name);

    const url = `genericdataimport/uploadfile?dataType=BuyerAddress&entityCode=${businessEntity}&regionId=${this.regions.selectedRegionId()}`;
    const queryParams = new HttpParams();
    queryParams.set('dataType', 'BuyerAddress');
    queryParams.set('entityCode', businessEntity);

    return this.http
      .post(url, formData, {
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        map((event) => {
          if (event.type === HttpEventType.UploadProgress) {
            return Math.round((100 * event.loaded) / (event.total || 1));
          } else {
            return 0;
          }
        }),
      );
  };

  uploadBuyerLogins = (file: File) => {
    const formData = new FormData();
    formData.append('file', file, file.name);

    const url = `genericdataimport/uploadfile?dataType=BuyerLogin&regionId=${this.regions.selectedRegionId()}`;

    return this.http
      .post(url, formData, {
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        map((event) => {
          if (event.type === HttpEventType.UploadProgress) {
            return Math.round((100 * event.loaded) / (event.total || 1));
          } else {
            return 0;
          }
        }),
      );
  };

  downloadBuyers() {
    return this.http.get('Buyer/download', {
      responseType: 'blob',
      observe: 'response',
    });
  }
}
